import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  getApiResponseMessage,
  getApiErrorMessage,
} from "../utils/apiMessageHelper";
import { Button, Card, Col, Form, Row } from "react-bootstrap";
import Container from "react-bootstrap/Container";
import Modal from "react-bootstrap/Modal";
import useStoredPermissions from "./useStoredPermissions";
import { DataTablePagination } from "./utils/dataTablePagination";
import { SideBar } from "./sideBar";
import { NavBar } from "./navBar";
import ClipLoader from "react-spinners/ClipLoader";
import { DropDownDepartments } from "./departments/dropDownDepartments";
import { DropDownClients } from "./clients/dropDownClients";
// Contexts
import { useNotification } from "../contexts/NotificationContext";
import { useAuth } from "../contexts/AuthContext";
// API
import {
  getNotificationSchedulesByClient,
  createNotificationSchedule,
  updateNotificationSchedule,
  deleteNotificationSchedule,
  toggleNotificationSchedule,
} from "../api/apiClient.js";

export const MainNotifications = () => {
  const { t } = useTranslation();
  const permissions = useStoredPermissions() || [];
  const { clientId } = useAuth();
  const { notify } = useNotification();

  // Show sidebar if user has clientId (both Super Admin and Client Admin)
  const hasPermissions = clientId !== null && clientId !== undefined;

  // Check if user is Super Admin (clientId === 1)
  const isSuperAdmin = clientId === 1;

  //> States
  // Selected client for Super Admin (default to their own clientId for Client Admin)
  const [selectedClientId, setSelectedClientId] = useState(
    isSuperAdmin ? null : clientId,
  );

  const [notificationRows, setNotificationRows] = useState([]);
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false);
  const [data, setData] = useState({});
  const [currentNotification, setCurrentNotification] = useState(null);
  const [showNewNotification, setShowNewNotification] = useState(false);
  const [showEditNotification, setShowEditNotification] = useState(false);
  const [showDeleteNotificationConfirm, setShowDeleteNotificationConfirm] =
    useState(false);
  const [newNotification, setNewNotification] = useState({
    idClient: selectedClientId,
    idDepartment: null,
    notificationTitle: "",
    notificationBody: "",
    notificationData: { url: "" },
    scheduleTime: "08:00",
    daysOfWeek: "",
    isActive: true,
  });
  const [daysOfWeekSelected, setDaysOfWeekSelected] = useState([]);

  //> Handlers
  const handleShowNewNotification = () => setShowNewNotification(true);

  const handleCloseNewNotification = () => {
    setShowNewNotification(false);
    setNewNotification({
      idClient: selectedClientId,
      idDepartment: null,
      notificationTitle: "",
      notificationBody: "",
      notificationData: { url: "" },
      scheduleTime: "08:00",
      daysOfWeek: "",
      isActive: true,
    });
    setDaysOfWeekSelected([]);
  };

  const handleShowEditNotification = () => {
    if (currentNotification) {
      // Parsear daysOfWeek string a array para los checkboxes
      const days = currentNotification.daysOfWeek
        ? currentNotification.daysOfWeek.split(",")
        : [];
      setDaysOfWeekSelected(days);

      // Asegurarse de que notificationData está en el formato correcto
      let notificationData = currentNotification.notificationData;
      if (typeof notificationData === "string") {
        try {
          notificationData = JSON.parse(notificationData);
        } catch (e) {
          notificationData = { url: null };
        }
      }
      if (!notificationData) {
        notificationData = { url: null };
      }

      // Actualizar currentNotification con notificationData parseado
      setCurrentNotification((prev) => ({
        ...prev,
        notificationData,
      }));

      setShowEditNotification(true);
    }
  };

  const handleCloseEditNotification = () => {
    setShowEditNotification(false);
    setDaysOfWeekSelected([]);
  };

  const handleShowDeleteNotification = () => {
    if (currentNotification) {
      setShowDeleteNotificationConfirm(true);
    }
  };

  const handleCloseDeleteNotification = () => {
    setShowDeleteNotificationConfirm(false);
  };

  const handleNewNotificationChange = (event) => {
    const { name, value, type, checked } = event.target;
    setNewNotification((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleNewNotificationDataChange = (event) => {
    const { name, value } = event.target;
    setNewNotification((prev) => ({
      ...prev,
      notificationData: {
        ...prev.notificationData,
        [name]: value,
      },
    }));
  };

  const handleEditNotificationChange = (event) => {
    const { name, value, type, checked } = event.target;
    setCurrentNotification((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleEditNotificationDataChange = (event) => {
    const { name, value } = event.target;
    setCurrentNotification((prev) => ({
      ...prev,
      notificationData: {
        ...prev.notificationData,
        [name]: value,
      },
    }));
  };

  const handleDayOfWeekToggle = (day, isNew = true) => {
    const dayStr = day.toString();
    if (isNew) {
      setDaysOfWeekSelected((prev) => {
        if (prev.includes(dayStr)) {
          return prev.filter((d) => d !== dayStr);
        } else {
          return [...prev, dayStr];
        }
      });
    }
  };

  //> API Calls
  async function getNotifications() {
    setIsLoadingNotifications(true);
    try {
      const res = await getNotificationSchedulesByClient(selectedClientId);
      if (res.success === false) {
        setData((prev) => ({
          ...prev,
          message: res.message,
        }));
        setNotificationRows([]);
      } else {
        // Procesar notificationData si viene como string JSON y convertir hora UTC a local
        const processedData = res.map((notification) => {
          let notificationData = notification.notificationData;

          // Si notificationData es un string, parsearlo
          if (typeof notificationData === "string") {
            try {
              notificationData = JSON.parse(notificationData);
            } catch (e) {
              notificationData = { url: null };
            }
          }

          // Si es null o undefined, usar objeto por defecto
          if (!notificationData) {
            notificationData = { url: null };
          }

          // Convertir scheduleTime de UTC a hora local para mostrar al usuario
          const localScheduleTime = notification.scheduleTime
            ? convertUTCToLocalTime(notification.scheduleTime)
            : notification.scheduleTime;

          return {
            ...notification,
            notificationData,
            scheduleTime: localScheduleTime, // Mostrar en hora local
          };
        });

        setNotificationRows(processedData);
      }
    } catch (e) {
      setData((prev) => ({
        ...prev,
        message: t("notifications.errorLoadingNotifications"),
      }));
      setNotificationRows([]);
    } finally {
      setIsLoadingNotifications(false);
    }
  }

  // Función para convertir hora local a UTC
  const convertLocalTimeToUTC = (localTime) => {
    // localTime viene en formato "HH:MM" (ej: "14:30")
    const [hours, minutes] = localTime.split(":").map(Number);

    // Crear fecha de hoy con la hora local seleccionada
    const localDate = new Date();
    localDate.setHours(hours, minutes, 0, 0);

    // Obtener la hora en UTC
    const utcHours = localDate.getUTCHours();
    const utcMinutes = localDate.getUTCMinutes();

    // Formatear a "HH:MM"
    return `${String(utcHours).padStart(2, "0")}:${String(utcMinutes).padStart(2, "0")}`;
  };

  // Función para convertir hora UTC a local
  const convertUTCToLocalTime = (utcTime) => {
    // utcTime viene en formato "HH:MM" (ej: "14:30")
    const [hours, minutes] = utcTime.split(":").map(Number);

    // Crear fecha de hoy con la hora UTC
    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes, 0, 0);

    // Obtener la hora local
    const localHours = utcDate.getHours();
    const localMinutes = utcDate.getMinutes();

    // Formatear a "HH:MM"
    return `${String(localHours).padStart(2, "0")}:${String(localMinutes).padStart(2, "0")}`;
  };

  // Función para convertir formato 24h a AM/PM usando método nativo de JavaScript
  const convertTo12HourFormat = (time24) => {
    // time24 viene en formato "HH:MM" (ej: "14:30")
    const [hours, minutes] = time24.split(":").map(Number);

    // Crear objeto Date y establecer la hora
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    // Usar toLocaleTimeString con formato AM/PM
    return date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const handleNewNotificationSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      notify("Por favor complete todos los campos requeridos", {
        type: "warning",
      });
      return;
    }

    try {
      // Construir el objeto notificationData correctamente
      const notificationDataObj = {
        url: newNotification.notificationData?.url || null,
      };

      // Convertir hora local a UTC antes de enviar al servidor
      const utcScheduleTime = convertLocalTimeToUTC(
        newNotification.scheduleTime,
      );

      // Convertir array de días seleccionados a string separado por comas
      const payload = {
        idClient: selectedClientId,
        idDepartment: newNotification.idDepartment || null,
        notificationTitle: newNotification.notificationTitle,
        notificationBody: newNotification.notificationBody,
        notificationData: notificationDataObj,
        scheduleTime: utcScheduleTime, // Enviar en UTC
        daysOfWeek: daysOfWeekSelected.join(","),
        isActive: newNotification.isActive,
      };

      const res = await createNotificationSchedule(payload);

      if (res.success) {
        const message =
          getApiResponseMessage(res) ||
          t("notifications.notificationCreatedSuccess");
        notify(message, { type: "success" });
        handleCloseNewNotification();
        getNotifications();
      } else {
        const errorMessage = getApiErrorMessage({ response: res });
        notify(errorMessage, { type: "error" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  };

  const handleEditNotificationSubmit = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    const form = event.currentTarget;
    if (form.checkValidity() === false) {
      notify("Por favor complete todos los campos requeridos", {
        type: "warning",
      });
      return;
    }

    try {
      // Construir el objeto notificationData correctamente
      const notificationDataObj = {
        url: currentNotification.notificationData?.url || null,
      };

      // Convertir hora local a UTC antes de enviar al servidor
      const utcScheduleTime = convertLocalTimeToUTC(
        currentNotification.scheduleTime,
      );

      const payload = {
        idClient: currentNotification.idClient || clientId,
        idDepartment: currentNotification.idDepartment || null,
        notificationTitle: currentNotification.notificationTitle,
        notificationBody: currentNotification.notificationBody,
        notificationData: notificationDataObj,
        scheduleTime: utcScheduleTime, // Enviar en UTC
        daysOfWeek: daysOfWeekSelected.join(","),
        isActive: currentNotification.isActive,
      };

      const res = await updateNotificationSchedule(
        currentNotification.id,
        payload,
      );

      if (res.success) {
        const message =
          getApiResponseMessage(res) ||
          t("notifications.notificationUpdatedSuccess");
        notify(message, { type: "success" });
        handleCloseEditNotification();
        getNotifications();
        setCurrentNotification(null);
      } else {
        const errorMessage = getApiErrorMessage({ response: res });
        notify(errorMessage, { type: "error" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  };

  const handleDeleteNotification = async () => {
    try {
      const res = await deleteNotificationSchedule(currentNotification.id);

      if (res.success) {
        const message =
          getApiResponseMessage(res) ||
          t("notifications.notificationDeletedSuccess");
        notify(message, { type: "success" });
      } else {
        const errorMessage = getApiErrorMessage({ response: res });
        notify(errorMessage, { type: "error" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    } finally {
      handleCloseDeleteNotification();
      getNotifications();
      setCurrentNotification(null);
    }
  };

  const handleToggleNotificationActive = async (
    notificationId,
    currentStatus,
  ) => {
    try {
      const res = await toggleNotificationSchedule(
        notificationId,
        !currentStatus,
      );

      if (res.success) {
        const message = getApiResponseMessage(res);
        notify(message, { type: "success" });
        getNotifications();
      } else {
        const errorMessage = getApiErrorMessage({ response: res });
        notify(errorMessage, { type: "error" });
      }
    } catch (e) {
      const errorMessage = getApiErrorMessage(e);
      notify(errorMessage, { type: "error" });
    }
  };

  const handleClientChange = (selected) => {
    if (selected) {
      // The dropdown now passes the full client object with idClient
      const newClientId = selected.idClient;
      // Allow setting to null when empty option is selected
      setSelectedClientId(newClientId);
      // Clear notifications when empty option is selected
      if (newClientId === null) {
        setNotificationRows([]);
      }
    }
  };

  //> Lifecycle
  useEffect(() => {
    if (selectedClientId) {
      getNotifications();
    }
  }, [selectedClientId]);

  //> Columns
  const notificationColumns = [
    {
      Header: " ",
      columns: [
        { Header: "ID", accessor: "id" },
        {
          Header: t("notifications.tableHeaderTitle"),
          accessor: "notificationTitle",
          Cell: (props) => {
            return <div className="cell-notification-title">{props.value}</div>;
          },
        },
        {
          Header: t("notifications.tableHeaderMessage"),
          accessor: "notificationBody",
          Cell: (props) => {
            const text = props.value || "";
            const truncated =
              text.length > 50 ? text.substring(0, 50) + "..." : text;
            return <div className="cell-notification-body">{truncated}</div>;
          },
        },
        {
          Header: t("notifications.tableHeaderTime"),
          accessor: "scheduleTime",
          Cell: (props) => {
            const time12h = props.value
              ? convertTo12HourFormat(props.value)
              : "";
            return <div className="cell-schedule-time">{time12h}</div>;
          },
        },
        {
          Header: t("notifications.tableHeaderDays"),
          accessor: "daysOfWeek",
          Cell: (props) => {
            const daysMap = {
              0: "Dom",
              1: "Lun",
              2: "Mar",
              3: "Mie",
              4: "Jue",
              5: "Vie",
              6: "Sab",
            };
            const days = props.value
              ? props.value
                  .split(",")
                  .map((d) => daysMap[d] || d)
                  .join(", ")
              : "";
            return <div className="cell-days-of-week">{days}</div>;
          },
        },
        {
          Header: t("notifications.tableHeaderState"),
          accessor: "isActive",
          Cell: (props) => {
            return (
              <Form.Check
                type="switch"
                checked={props.value}
                onChange={() =>
                  handleToggleNotificationActive(
                    props.row.original.id,
                    props.value,
                  )
                }
              />
            );
          },
        },
      ],
    },
  ];

  return (
    <React.Fragment>
      {hasPermissions && <SideBar />}
      <Container>
        <NavBar />
        <Container>
          <Row>
            <Card>
              <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                  <Card.Title as="h5">
                    {t("notifications.pageTitle")}
                  </Card.Title>
                  {isSuperAdmin && (
                    <div style={{ minWidth: "250px" }}>
                      <DropDownClients
                        value={selectedClientId}
                        onChange={handleClientChange}
                        className="form-control"
                      />
                    </div>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                <div id="flexDiv">
                  <Button
                    id="btnLarge"
                    variant="primary"
                    onClick={handleShowNewNotification}
                    disabled={!permissions.includes("Add/Update Notification")}
                  >
                    {t("common.add")}
                  </Button>
                  {currentNotification && (
                    <>
                      <Button
                        id="btnLarge"
                        variant="warning"
                        onClick={handleShowEditNotification}
                        disabled={
                          !permissions.includes("Add/Update Notification")
                        }
                        style={{ marginLeft: "10px" }}
                      >
                        {t("common.edit")}
                      </Button>
                      <Button
                        id="btnLarge"
                        variant="danger"
                        onClick={handleShowDeleteNotification}
                        disabled={!permissions.includes("Delete Notification")}
                        style={{ marginLeft: "10px" }}
                      >
                        {t("common.delete")}
                      </Button>
                    </>
                  )}
                </div>
                <Col xs={12}>
                  {isLoadingNotifications ? (
                    <div className="load-icon">
                      <ClipLoader
                        size={50}
                        color={"#0b5ed7"}
                        loading={isLoadingNotifications}
                      />
                    </div>
                  ) : notificationRows.length === 0 ? (
                    <div className="no-data">
                      {data?.message ||
                        t("notifications.noNotificationsScheduled")}
                    </div>
                  ) : (
                    <DataTablePagination
                      columns={notificationColumns}
                      data={notificationRows}
                      onRowClick={(selectedRow) =>
                        setCurrentNotification(selectedRow)
                      }
                      hiddenColumns={["id"]}
                    />
                  )}
                </Col>
              </Card.Body>
            </Card>
          </Row>
        </Container>
      </Container>

      {/* Modal: Create New Notification */}
      <Modal
        show={showNewNotification}
        onHide={handleCloseNewNotification}
        size="lg"
      >
        <Form
          className="addForm"
          onSubmit={handleNewNotificationSubmit}
          noValidate
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("notifications.newNotificationModalTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("notifications.titleLabel")} *</Form.Label>
                  <Form.Control
                    required
                    type="text"
                    name="notificationTitle"
                    value={newNotification.notificationTitle}
                    onChange={handleNewNotificationChange}
                    placeholder={t("notifications.titlePlaceholder")}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("notifications.departmentOptional")}
                  </Form.Label>
                  <DropDownDepartments
                    value={newNotification.idDepartment}
                    idClient={selectedClientId}
                    allowEmpty={true}
                    onChange={(selected) => {
                      setNewNotification((prev) => ({
                        ...prev,
                        idDepartment: selected ? selected.value : null,
                      }));
                    }}
                  />
                  <Form.Text className="text-muted">
                    {t("notifications.departmentHelp")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("notifications.messageLabel")} *</Form.Label>
                  <Form.Control
                    required
                    as="textarea"
                    rows={3}
                    name="notificationBody"
                    value={newNotification.notificationBody}
                    onChange={handleNewNotificationChange}
                    placeholder={t("notifications.messagePlaceholder")}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("notifications.sendTimeLabel")} *</Form.Label>
                  <Form.Control
                    required
                    type="time"
                    name="scheduleTime"
                    value={newNotification.scheduleTime}
                    onChange={handleNewNotificationChange}
                  />
                  <Form.Text className="text-muted">
                    {t("notifications.localTimezoneHelp")}
                  </Form.Text>
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t("notifications.urlOptional")}</Form.Label>
                  <Form.Control
                    type="text"
                    name="url"
                    value={newNotification.notificationData?.url || ""}
                    onChange={handleNewNotificationDataChange}
                    placeholder={t("notifications.urlPlaceholder")}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Label>
                    {t("notifications.daysOfWeekLabel")} *
                  </Form.Label>
                  <div className="d-flex flex-wrap gap-2">
                    {[
                      { value: "0", label: t("common.sunday") },
                      { value: "1", label: t("common.monday") },
                      { value: "2", label: t("common.tuesday") },
                      { value: "3", label: t("common.wednesday") },
                      { value: "4", label: t("common.thursday") },
                      { value: "5", label: t("common.friday") },
                      { value: "6", label: t("common.saturday") },
                    ].map((day) => (
                      <Form.Check
                        key={day.value}
                        type="checkbox"
                        id={`new-day-${day.value}`}
                        label={day.label}
                        checked={daysOfWeekSelected.includes(day.value)}
                        onChange={() => handleDayOfWeekToggle(day.value, true)}
                      />
                    ))}
                  </div>
                  <Form.Text className="text-muted">
                    {t("notifications.daysOfWeekHelp")}
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col md={12}>
                <Form.Group className="mb-3">
                  <Form.Check
                    type="checkbox"
                    name="isActive"
                    label={t("notifications.activeLabel")}
                    checked={newNotification.isActive}
                    onChange={handleNewNotificationChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseNewNotification}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" type="submit">
              {t("common.save")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Edit Notification */}
      <Modal
        show={showEditNotification}
        onHide={handleCloseEditNotification}
        size="lg"
      >
        <Form
          className="addForm"
          onSubmit={handleEditNotificationSubmit}
          noValidate
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("notifications.editNotificationModalTitle")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {currentNotification && (
              <>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t("notifications.titleLabel")} *</Form.Label>
                      <Form.Control
                        required
                        type="text"
                        name="notificationTitle"
                        value={currentNotification.notificationTitle || ""}
                        onChange={handleEditNotificationChange}
                        placeholder={t("notifications.titlePlaceholder")}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t("notifications.departmentOptional")}
                      </Form.Label>
                      <DropDownDepartments
                        value={currentNotification.idDepartment}
                        idClient={selectedClientId}
                        allowEmpty={true}
                        onChange={(selected) => {
                          setCurrentNotification((prev) => ({
                            ...prev,
                            idDepartment: selected ? selected.value : null,
                          }));
                        }}
                      />
                      <Form.Text className="text-muted">
                        {t("notifications.departmentHelp")}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t("notifications.messageLabel")} *
                      </Form.Label>
                      <Form.Control
                        required
                        as="textarea"
                        rows={3}
                        name="notificationBody"
                        value={currentNotification.notificationBody || ""}
                        onChange={handleEditNotificationChange}
                        placeholder={t("notifications.messagePlaceholder")}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t("notifications.sendTimeLabel")} *
                      </Form.Label>
                      <Form.Control
                        required
                        type="time"
                        name="scheduleTime"
                        value={currentNotification.scheduleTime || ""}
                        onChange={handleEditNotificationChange}
                      />
                      <Form.Text className="text-muted">
                        {t("notifications.localTimezoneHelp")}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t("notifications.urlOptional")}</Form.Label>
                      <Form.Control
                        type="text"
                        name="url"
                        value={currentNotification.notificationData?.url || ""}
                        onChange={handleEditNotificationDataChange}
                        placeholder={t("notifications.urlPlaceholder")}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {t("notifications.daysOfWeekLabel")} *
                      </Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {[
                          { value: "0", label: t("common.sunday") },
                          { value: "1", label: t("common.monday") },
                          { value: "2", label: t("common.tuesday") },
                          { value: "3", label: t("common.wednesday") },
                          { value: "4", label: t("common.thursday") },
                          { value: "5", label: t("common.friday") },
                          { value: "6", label: t("common.saturday") },
                        ].map((day) => (
                          <Form.Check
                            key={day.value}
                            type="checkbox"
                            id={`edit-day-${day.value}`}
                            label={day.label}
                            checked={daysOfWeekSelected.includes(day.value)}
                            onChange={() =>
                              handleDayOfWeekToggle(day.value, true)
                            }
                          />
                        ))}
                      </div>
                      <Form.Text className="text-muted">
                        {t("notifications.daysOfWeekHelp")}
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        name="isActive"
                        label={t("notifications.activeLabel")}
                        checked={currentNotification.isActive}
                        onChange={handleEditNotificationChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>
              </>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseEditNotification}>
              {t("common.cancel")}
            </Button>
            <Button variant="primary" type="submit">
              {t("common.saveChanges")}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Modal: Delete Notification Confirmation */}
      <Modal
        show={showDeleteNotificationConfirm}
        onHide={handleCloseDeleteNotification}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {t("notifications.deleteNotificationModalTitle")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("notifications.deleteConfirmationMessage")}
          {currentNotification && (
            <div className="mt-3">
              <strong>{t("notifications.titleLabel")}:</strong>{" "}
              {currentNotification.notificationTitle}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteNotification}>
            {t("common.cancel")}
          </Button>
          <Button variant="danger" onClick={handleDeleteNotification}>
            {t("common.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
};
